import { Trans, t } from "@lingui/macro";
import { PlusCircle, Loader2, MinusCircle, FileText, Download } from "lucide-react";
import { useEffect, useState } from "react";
import { Soribot } from "../../models/Soribot";
import { SourceUploadedFile } from "../../models/SoribotSource";
import { Fire } from "../../services/FireClient";
import { My } from "../../services/My";
import { UtilApp } from "../../services/Utils/UtilApp";
import { UploadFileSection, UploadedFile } from "./compontents/UploadFileSection";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../../components/ui/accordion";
import { Button } from "../../components/ui/button";
import { useToast } from "../../hooks/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { Progress } from "../../components/ui/progress";

export const SoribotDatasourcesUploadFiles: React.FC<{ bot: Soribot }> = ({ bot }) => {
  const [inProgress, setInProgress] = useState(false);
  const { toast } = useToast();
  const [sourceFiles, setSourceFiles] = useState<SourceUploadedFile[]>([]);
  const [removedSourceFiles, setRemovedSourceFiles] = useState<SourceUploadedFile[]>([]);
  const [inProgressUpdate, setInProgressUpdate] = useState(false);
  const [accordionValue, setAccordionValue] = useState<string | undefined>(undefined);
  const [alert, setAlert] = useState<{ open: boolean, title: string, message: React.ReactNode, confirm: (() => void) | null }>({ open: false, title: '', message: '', confirm: null });

  useEffect(() => {
    setSourceFiles([]);
    reloadSourceFiles();
  }, [bot.id]);

  return <>
    <Accordion type="single" collapsible value={accordionValue} onValueChange={setAccordionValue} className="w-full bg-background rounded border mb-4">
      <AccordionItem value="add-files" className="border-none">
        <AccordionTrigger className="px-4 hover:no-underline text-sm font-semibold">
          <div className="flex items-center"><PlusCircle className="w-4 h-4 mr-2" /> <Trans>Add files from your computer</Trans></div>
        </AccordionTrigger>
        <AccordionContent className="px-4 pb-4">
          <div className="p-4 border rounded bg-muted/50 text-center">
            <UploadFileSection storageFolderPath={`/${Soribot.COL}/${bot.id}/files`} onFilesUploaded={onFilesUploadedHandler} />
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>

    {inProgress && <div className="p-4 flex justify-center"><Loader2 className="animate-spin text-muted-foreground" /></div>}
    {!inProgress && <>
      <h4 className="text-sm font-bold text-muted-foreground px-2 mt-4 uppercase"><Trans>Added files</Trans></h4>
      {sourceFiles.length === 0 && <div className="p-4 text-center text-muted-foreground"><Trans>No files added</Trans></div>}
      <div className="space-y-2 mt-2">
        {sourceFiles.map(p => sourceFileItem(p))}
      </div>
      <br />
      <div className="px-2">
        <Button disabled={inProgressUpdate} onClick={updateBotContent}><Trans>Sync bot knowledge</Trans></Button>
      </div>
    </>}

    <AlertDialog open={alert.open} onOpenChange={(open) => setAlert(prev => ({ ...prev, open }))}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{alert.title}</AlertDialogTitle>
          <AlertDialogDescription>{alert.message}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction onClick={() => setAlert(prev => ({ ...prev, open: false }))}><Trans>OK</Trans></AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </>

  function sourceFileItem(p: SourceUploadedFile) {
    const toRemove = removedSourceFiles.some(f => f.fileUrl === p.fileUrl);
    return <div key={p.id} className={`flex items-center justify-between p-3 rounded border bg-background ${toRemove ? 'opacity-50' : 'hover:bg-muted/50'}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 font-medium truncate text-sm">
          <a href={p.fileUrl} target="_blank" rel="noreferrer" className="flex items-center hover:underline text-blue-600">
            <FileText className="w-4 h-4 mr-1" />{p.fileName}
          </a>
        </div>
        <div className="text-xs text-muted-foreground mt-1 truncate pl-5">
          {toRemove ? <span className="text-destructive"><Trans>Sync to remove</Trans></span>
            :
            <>{p.grounded ? <Trans>Synced {UtilApp.dateStrToTimeAndDay(p.grounded, My.user.lang)}</Trans>
              :
              <span className="text-destructive"><Trans>Awaiting sync</Trans></span>}</>
          }
        </div>
      </div>
      {inProgressUpdate ?
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground shrink-0" />
        :
        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10 shrink-0" onClick={() => removeSourceFile(p)}>
          <MinusCircle className="w-5 h-5" />
        </Button>
      }
    </div>
  }

  async function reloadSourceFiles() {
    setInProgress(true);
    const files = await Fire.bot.getSourceFiles(bot.id)
    setSourceFiles(files);
    setInProgress(false);
  }

  async function onFilesUploadedHandler(files: UploadedFile[]) {
    const moreFiles = files.map(f => new SourceUploadedFile(bot.id, My.user.id, f.fileName, f.fileUrl, f.fileSize, f.fileType));
    setSourceFiles(sourceFiles.concat(moreFiles));
    await Fire.bot.saveSourceFiles(bot.id, moreFiles);
  }

  async function updateBotContent() {
    setInProgressUpdate(true);
    try {
      await Fire.bot.deleteSources(removedSourceFiles);
      await Fire.bot.updateSoribotContent({ soribotId: bot.id, type: 'file-bot-upload' });
      setAlert({ open: true, title: t`Done!`, message: t`The knowledge content is updated. The result can be viewed in the Knowledge viewer.`, confirm: null });
      setRemovedSourceFiles([]);
    } catch (error: any) {
      toast({ variant: "destructive", title: error.message });
    } finally {
      reloadSourceFiles();
      setInProgressUpdate(false);
    }
  }

  async function removeSourceFile(p: SourceUploadedFile) {
    setRemovedSourceFiles([...removedSourceFiles, p]);
  }
}